<script setup>
import { computed, getCurrentInstance } from 'vue';
import Button from 'primevue/button';
import { useCurrencyFormatter } from '@/utils/currencyFormatter.js';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const { formatConvertedPrice } = useCurrencyFormatter();

const props = defineProps({
    bestSellers: { type: Array, default: () => [] },
    clientHistory: { type: Array, default: () => [] },
    clientHistoryLoading: { type: Boolean, default: false },
    recommended: { type: Array, default: () => [] },
    clientSelected: { type: Boolean, default: false },
    existingSiteIds: { type: Array, default: () => [] },
});

const emit = defineEmits(['add', 'add-all']);

const sections = computed(() => [
    {
        key: 'best_sellers',
        label: $t('Best sellers'),
        icon: 'pi pi-star',
        color: 'text-yellow-500',
        sites: props.bestSellers,
        loading: false,
    },
    {
        key: 'client_history',
        label: $t('Client history'),
        icon: 'pi pi-history',
        color: 'text-blue-500',
        sites: props.clientHistory,
        loading: props.clientHistoryLoading,
        empty: props.clientSelected ? $t('No suggestions available') : $t('Select a client to see history'),
        showPlaceholder: !props.clientSelected,
    },
    {
        key: 'recommended',
        label: $t('Recommended'),
        icon: 'pi pi-thumbs-up',
        color: 'text-green-500',
        sites: props.recommended,
        loading: false,
    },
]);

const isAlreadyAdded = (siteId) => props.existingSiteIds.includes(siteId);

const handleAdd = (site) => emit('add', site);

const handleAddAll = (sites) => {
    const toAdd = sites.filter(s => !isAlreadyAdded(s.id));
    if (toAdd.length) emit('add-all', toAdd);
};
</script>

<template>
    <div class="mt-5 border-t border-surface-200 pt-4">
        <!-- Header -->
        <div class="flex items-center gap-2 mb-3">
            <i class="pi pi-lightbulb text-primary-500"></i>
            <span class="font-semibold text-surface-700">{{ $t('Suggestions') }}</span>
        </div>

        <!-- Sections -->
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div v-for="section in sections" :key="section.key" class="border border-surface-200 rounded-lg overflow-hidden">
                <!-- Section header -->
                <div class="flex items-center justify-between px-3 py-2 bg-surface-50 border-b border-surface-200">
                    <div class="flex items-center gap-1.5">
                        <i :class="[section.icon, section.color, 'text-sm']"></i>
                        <span class="font-medium text-sm">{{ section.label }}</span>
                        <span v-if="section.sites.length" class="text-xs text-surface-400">({{ section.sites.length }})</span>
                    </div>
                    <Button
                        v-if="section.sites.filter(s => !isAlreadyAdded(s.id)).length > 0"
                        :label="$t('Add all')"
                        icon="pi pi-plus"
                        size="small"
                        text
                        severity="secondary"
                        class="p-0 text-xs"
                        @click="handleAddAll(section.sites)"
                    />
                </div>

                <!-- Loading -->
                <div v-if="section.loading" class="flex items-center justify-center py-4">
                    <i class="pi pi-spin pi-spinner text-surface-400"></i>
                </div>

                <!-- Placeholder / empty -->
                <div
                    v-else-if="section.sites.length === 0"
                    class="flex flex-col items-center justify-center py-5 px-3 text-center"
                >
                    <i v-if="section.showPlaceholder" class="pi pi-user text-surface-300 text-2xl mb-2"></i>
                    <span class="text-xs text-surface-400 italic">{{ section.empty || $t('No suggestions available') }}</span>
                </div>

                <!-- Site rows -->
                <ul v-else class="divide-y divide-surface-100">
                    <li
                        v-for="site in section.sites"
                        :key="site.id"
                        class="flex items-center justify-between px-3 py-2 hover:bg-surface-50 transition-colors gap-2"
                    >
                        <div class="flex-1 min-w-0">
                            <div class="text-sm font-medium truncate" :title="site.url">{{ site.url }}</div>
                            <div class="flex items-center gap-2 text-xs text-surface-500 mt-0.5">
                                <span>DA {{ Math.round(site.da || 0) }}</span>
                                <span>DR {{ Math.round(site.dr || 0) }}</span>
                                <span
                                    v-if="site.selected_offer"
                                    class="font-semibold text-primary-600 whitespace-nowrap"
                                >
                                    {{ formatConvertedPrice(site.selected_offer.converted_price, site.selected_offer.converted_coin) }}
                                </span>
                            </div>
                            <div v-if="site.selected_offer?.seller?.name" class="text-xs text-surface-400 truncate">
                                {{ site.selected_offer.seller.name }}
                            </div>
                        </div>

                        <Button
                            icon="pi pi-plus"
                            size="small"
                            rounded
                            :severity="isAlreadyAdded(site.id) ? 'secondary' : 'success'"
                            :disabled="isAlreadyAdded(site.id)"
                            @click="handleAdd(site)"
                        />
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>
